import React from "react"
import { Table, TableBody, TableFooter, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from "material-ui/Table"
import { TextField, FlatButton, CircularProgress, FontIcon, IconButton, Toggle, Tabs, Tab, Drawer, Card, CardHeader } from "material-ui"
import AppStateStore from "../../../stores/AppStateStore"
import strings from "../../../localization/strings"
import { amber50, green500, red900 } from "material-ui/styles/colors"
import { IScheduleDocument, ISchedulePatternTemplate, IShiftCandidate, IWorkPoolEmployee, IScheduleConstraint, IScheduleShift, EScheduleConstraintSeverity, EScheduleConstraintType, IShiftCandidateSearch, IJobDocument, IUserDocument, IAssignedEmployeeConstraint } from "../../../models"
import moment from "moment"
import autobind from "class-autobind"
import * as utils from "../../../helpers/utils"
import AccountStore from "../../../stores/AccountStore"
import * as scheduleHelper from "../../../helpers/scheduleHelper"
import * as jobHelper from "../../../helpers/jobHelper"
import * as permissionHelper from "../../../helpers/permissionHelper"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import * as api from "../../../api"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import _ from "lodash"
import SelectAdditionalDaysDialog from "./SelectAdditionalDaysDialog"
import CheckAssignedEmployeeConstraintDialog from "./CheckAssignedEmployeeConstraintDialog"
import ShiftWorkerPool from "./ShiftWorkerPool"
import Clear from "../../../components/misc/Clear"
import ImageLoader from "../../misc/ImageLoader"
import ConfirmDialog from "../../common/ConfirmDialog"

interface IProps {
	open: boolean
	schedule: IScheduleDocument
	assignPatternTemplate: ISchedulePatternTemplate
	handleClose(): void
	handleAssign(shiftIds: string[], assignEmployeeId: string): void
	replaceSchedulePatternTemplate?(schedulePatternTemplate: ISchedulePatternTemplate): void

}
interface IState {
	candidates: IShiftCandidate[] | IWorkPoolEmployee[]
	search: string
	offset: number
	loading: boolean
	total: number

	filterByDefaultHierarchy: boolean
	//both limitToShiftHierarchy and limitByJobShortlist cannot be true at same time.
	limitToShiftHierarchy: boolean
	limitByJobShortlist: boolean //it's valid for CWM enabled case

	limitToShiftPosition: boolean


	selectedEmployee: IShiftCandidate | IWorkPoolEmployee
	tabActive: string

	openSelectAdditionalDaysDialog: boolean
	selectedDays: ISchedulePatternTemplate[]
	employeeConstraints: IScheduleConstraint[]
	confirmCandidate: boolean
}

export default class AssignEmployeeDrawer extends React.Component<IProps, IState> {
	private limit
	private jobId
	private requireMatchingEmployeeShiftPosition
	private availablePatternTemplates: ISchedulePatternTemplate[]
	constructor(props: IProps) {
		super(props)
		this.jobId = this.props.assignPatternTemplate && this.props.assignPatternTemplate.ShiftTemplates && this.props.assignPatternTemplate.ShiftTemplates.length &&
			this.props.assignPatternTemplate.ShiftTemplates[0].JobId
		this.requireMatchingEmployeeShiftPosition = props.assignPatternTemplate.ShiftTemplates.find(st => st.MatchEmployeePosition && st.Position) ? true : false

		const storedFilterByDefaultHierarchy = utils.getCookie("AssignEmployeeFilterByDefaultHierarchy")
		const storedLimitToShiftHierarchy = utils.getCookie("AssignEmployeeLimitToShiftHierarchy")
		const storedLimitByJobShortlist = utils.getCookie("AssignEmployeeLimitByJobShortlist")
		const storedLimitToShiftPosition = utils.getCookie("AssignEmployeeLimitToShiftPosition")

		this.state = {
			candidates: [],
			search: "",
			offset: 0,
			loading: true,
			total: 0,
			filterByDefaultHierarchy: storedFilterByDefaultHierarchy === "true",
			limitToShiftHierarchy: storedLimitToShiftHierarchy === "true",
			limitByJobShortlist: this.jobId && AccountStore.getCRMEnabled() && storedLimitByJobShortlist === "true",
			limitToShiftPosition: this.requireMatchingEmployeeShiftPosition && storedLimitToShiftPosition === "true",
			selectedEmployee: null,
			tabActive: "all_employees",
			openSelectAdditionalDaysDialog: false,
			selectedDays: [props.assignPatternTemplate],
			employeeConstraints: [],
			confirmCandidate: false
		}
		autobind(this)
		const tempLimit = Math.floor(window.innerHeight / 50) - 9
		this.limit = tempLimit > 8 ? 8 : tempLimit
		this.availablePatternTemplates = scheduleHelper.getCopiedUnassignedPTs(props.schedule, props.assignPatternTemplate)
	}

	componentDidMount() {
		this.getCandidates()
	}

	getShiftStartLocalTimeUTC(assignPatternTemplate: ISchedulePatternTemplate) {
		let startShift: IScheduleShift
		let startTime
		assignPatternTemplate.ShiftTemplates.forEach(shift => {
			if (startTime) {
				if (moment(shift.StartDateTime).isSameOrBefore(startTime)) {
					startTime = shift.StartDateTime
					startShift = shift
				}
			}
			else {
				startTime = shift.StartDateTime
				startShift = shift
			}
		})
		const startSimpleTime = startShift.StartTime
		return moment.utc().year(startSimpleTime.Year).month(startSimpleTime.Month).date(startSimpleTime.Day).hour(startSimpleTime.Hour).minute(startSimpleTime.Minute).second(0).format();
	}

	getCandidates() {
		if (this.state.limitByJobShortlist) {
			api.getJob(this.jobId).then(res => {

				const filteredJobShortListCandidates = jobHelper.getShortListCandidates((res.data as IJobDocument).WorkPoolEmployees).filter(i => i.EmployeeName.toLowerCase().includes(this.state.search.toLowerCase()))
				const candidates = filteredJobShortListCandidates.filter((v, index) => index >= this.state.offset && index < this.state.offset + this.limit)
				this.setState({
					candidates,
					total: filteredJobShortListCandidates.length,
					loading: false
				})
			})
		}
		else {
			api.getScheduleAssignCandidates(
				this.getShiftStartLocalTimeUTC(this.props.assignPatternTemplate),
				this.state.offset,
				this.limit,
				this.state.search,
				this.props.assignPatternTemplate.HierarchyId,
				AccountStore.getSupervisorDirectReportEnabled() && !permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees),
				this.state.filterByDefaultHierarchy,
				this.state.limitToShiftHierarchy,
				this.state.limitToShiftPosition,
				this.state.limitToShiftPosition ? this.props.assignPatternTemplate.ShiftTemplates[0].Position.BasePositionId : ""
			).then(res => {
				const searchRes = res.data as IShiftCandidateSearch
				this.setState({
					candidates: searchRes.Candidates,
					total: searchRes.TotalCount,
					loading: false
				})
			})
		}
	}

	offsetChange(offset) {
		this.setState({
			offset,
			loading: true,
			selectedEmployee: null,
			employeeConstraints: []
		}, () =>
			this.getCandidates()
		)
	}

	missingBadges(missingBadges) {
		if (missingBadges && !missingBadges.length) {
			return (
				<div>
					<FontIcon className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
					<span style={{ paddingLeft: 5, color: green500 }}>{strings.None}</span>
				</div>
			)
		} else if (missingBadges && missingBadges.length === 1) return missingBadges[0]
		else if (missingBadges && missingBadges.length > 1) return missingBadges.join(", ")
	}

	handleSearch = _.debounce(() => this.getCandidates(), 1000)

	async handleClickSave(checkValidate: boolean = true) {
		const isValidate = checkValidate ?
			await this.checkValidate() :
			true
		if (!isValidate) return
		this.handleAssign()
	}

	handleAssign() {
		this.props.handleAssign(this.state.selectedDays.map(i => i.ShiftTemplates[0].Id), this.state.selectedEmployee.Id)
	}

	async checkValidate() {
		let isValidate = true
		if (!this.state.selectedEmployee) {
			isValidate = false
		}

		const employeeConstraints = await this.getEmployeeScheduleShiftConstraints()
		if (employeeConstraints.length > 0) {
			isValidate = false
		}

		return isValidate
	}

	async getEmployeeScheduleShiftConstraints() {
		if (!this.state.selectedEmployee) {
			this.setState({ employeeConstraints: [] })
			return []
		}

		AppStateStore.setProgressSnackbarContent("loading")
		if (this.props.schedule.MasterSchedule) {
			const candidateShifts: IScheduleShift[] = []
			this.state.selectedDays.forEach(i =>
				i.ShiftTemplates.forEach(s => {
					s.UserId = i.AssignedUserId
					candidateShifts.push(s)
				})
			)

			const assignedShifts: IScheduleShift[] = []
			this.props.schedule.Schedules.forEach(i =>
				i.AssignedUserId && !i.Deleted &&
				i.ShiftTemplates.forEach(s => {
					s.UserId = i.AssignedUserId
					!s.Deleted &&
						assignedShifts.push(s)
				})
			)

			const constraintShifts = assignedShifts.filter(i =>
				i.UserId === this.state.selectedEmployee.Id &&
				!candidateShifts.map(s => s.Id).includes(i.Id) &&
				candidateShifts.find(s =>
					(moment(s.StartDateTime).isSameOrAfter(i.StartDateTime) && moment(s.StartDateTime).isBefore(i.EndDateTime)) ||
					(moment(s.EndDateTime).isAfter(i.StartDateTime) && moment(s.EndDateTime).isSameOrBefore(i.EndDateTime)) ||
					(moment(s.StartDateTime).isSameOrBefore(i.StartDateTime) && moment(s.EndDateTime).isAfter(i.StartDateTime))
				)
			)
			const selectedEmployeeName = this.getSelectedEmployeeName()
			const employeeConstraints = constraintShifts.map(i => {
				return {
					UserId: this.state.selectedEmployee.Id,
					Severity: EScheduleConstraintSeverity.Error,
					Type: EScheduleConstraintType.ConflictsWithSchedule,
					Title: `cannot assign Employee ${selectedEmployeeName}`,
					Note: `Employee ${selectedEmployeeName} is already scheduled to the shifts of this master schedule.`
				} as IScheduleConstraint
			})
			if (employeeConstraints && employeeConstraints.length > 0) {
				AppStateStore.setProgressSnackbarContent(null)
				this.setState({ employeeConstraints })
			}
			else {
				const response = await api.getEmployeeScheduleShiftConstraints(this.state.selectedEmployee.Id, this.state.selectedDays, this.props.schedule.MasterSchedule)
				const employeeConstraints = response.data as IScheduleConstraint[]
				const employeeMasterScheduleConstraint = employeeConstraints.find(i => i.Type === EScheduleConstraintType.ConflictsWithSchedule)
				AppStateStore.setProgressSnackbarContent(null)
				if (employeeMasterScheduleConstraint) {
					AppStateStore.setConfirmDialogContent({
						title: strings.Warning,
						confirmBtnLabel: strings.Proceed,
						cancelBtnLabel: strings.Close,
						content: `${employeeMasterScheduleConstraint.Note} Adding them here may create an overlap. Do you wish to proceed?`,
						confirm: () => this.handleClickSave(false)

					})
					return [employeeMasterScheduleConstraint]
				}
				else {
					this.setState({ employeeConstraints: [] })
					return []
				}
			}
		}
		else {
			const response = await api.getEmployeeScheduleShiftConstraints(this.state.selectedEmployee.Id, this.state.selectedDays, this.props.schedule.MasterSchedule)
			const employeeConstraints = response.data as IScheduleConstraint[]
			AppStateStore.setProgressSnackbarContent(null)
			this.setState({ employeeConstraints })
			return employeeConstraints
		}
	}

	getSelectedEmployeeName() {
		return this.state.selectedEmployee ?
			this.state.selectedEmployee.hasOwnProperty("EmployeeName") ?
				(this.state.selectedEmployee as IWorkPoolEmployee).EmployeeName :
				`${(this.state.selectedEmployee as IShiftCandidate).FirstName} ${(this.state.selectedEmployee as IShiftCandidate).LastName}` :
			""
	}

	render() {
		return (
			<Drawer
				open={this.props.open}

				containerStyle={{
					zIndex: 1500,
					overflow: "auto",
					opacity: 1,
					transition: "opacity 200ms, transform 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
					width: "80vw"
				}}
				docked={false}

				onRequestChange={this.props.handleClose}
				openSecondary
				overlayStyle={{ zIndex: 1500 }}

			>
				<Card style={{ boxShadow: "none" }}>
					<CardHeader
						style={{ backgroundColor: AppStateStore.getDrawerHeaderColor(), height: 90, textAlign: "center" }}
						title={<h2 title={strings.Choose_Employee}>{strings.Choose_Employee}</h2>}
						children={
							<FontIcon
								data-intercom-target={"Notes-Btn-Close-Note-Drawer"}
								onTouchTap={this.props.handleClose}
								className="fa fa-close"
								style={{ cursor: "pointer", fontSize: 25, color: amber50, position: "relative", top: 10, right: "-30%" }}
							/>
						}
						titleColor={"white"}
					/>
				</Card>
				<Tabs
					value={this.state.tabActive}
					onChange={tabActive => this.setState({ tabActive })}
					style={{ flexGrow: 1, flex: 1 }}

				>
					<Tab label={strings.All_Employees} value="all_employees">
						<div style={{ padding: 20, overflow: "auto" }}>
							<Toggle
								data-intercom-target={"ChooseEmployee-Toggle-LimitToShiftHierarchy"}
								label={strings.Limit_To_Shift_Hierarchy}
								toggled={this.state.limitToShiftHierarchy}
								onToggle={(e, value) => {
									const limitByJobShortlist = value ? false : this.state.limitByJobShortlist
									this.setState({
										limitToShiftHierarchy: value,
										limitByJobShortlist,
										loading: true,
										offset: 0,
										selectedEmployee: null,
										employeeConstraints: []
									}, () =>
										this.getCandidates()
									)
									localStorage.setItem("findd_AssignEmployeeLimitToShiftHierarchy", value)
									localStorage.setItem("findd_AssignEmployeeLimitByJobShortlist", limitByJobShortlist as any)
								}}
								style={{ width: 250 }}
							/>
							<Toggle
								data-intercom-target={"ChooseEmployee-Toggle-FilterByDefaultHierarchy"}
								label={strings.Filter_By_Default_Hierarchy}
								toggled={this.state.filterByDefaultHierarchy}
								onToggle={(e, value) => {
									this.setState({
										filterByDefaultHierarchy: value,
										loading: true,
										offset: 0,
										selectedEmployee: null,
										employeeConstraints: []
									}, () =>
										this.getCandidates()
									)
									localStorage.setItem("findd_AssignEmployeeFilterByDefaultHierarchy", value)
								}}
								style={{ width: 250 }}
							/>
							{this.jobId && AccountStore.getCRMEnabled() &&
								<Toggle
									data-intercom-target={"ChooseEmployee-Toggle-LimitByJobShortlist"}
									label={strings.Limit_By_Job_Shortlist}
									toggled={this.state.limitByJobShortlist}
									onToggle={(e, value) => {
										const limitToShiftHierarchy = value ? false : this.state.limitToShiftHierarchy
										this.setState({
											limitByJobShortlist: value,
											limitToShiftHierarchy,
											loading: true,
											offset: 0,
											selectedEmployee: null,
											employeeConstraints: []
										}, () =>
											this.getCandidates()
										)
										localStorage.setItem("findd_AssignEmployeeLimitByJobShortlist", value)
										localStorage.setItem("findd_AssignEmployeeLimitToShiftHierarchy", limitToShiftHierarchy as any)
									}}
									style={{ width: 250, marginTop: 5 }}
								/>
							}
							{this.requireMatchingEmployeeShiftPosition &&
								<Toggle
									data-intercom-target={"ChooseEmployee-Toggle-LimitToShiftPosition"}
									disabled
									label={strings.Limit_To_Shift_Position}
									toggled={this.state.limitToShiftPosition}
									onToggle={(e, value) => {
										this.setState({
											limitToShiftPosition: value,
											loading: true,
											offset: 0,
											selectedEmployee: null,
											employeeConstraints: []
										}, () =>
											this.getCandidates()
										)
										localStorage.setItem("findd_AssignEmployeeLimitToShiftPosition", value)
									}}
									style={{ width: 250, marginTop: 5 }}
								/>
							}
							<div style={{ marginTop: 10 }}>
								<i style={{ fontSize: 25 }} className="fa fa-search" />
								<TextField
									data-intercom-target={"ChooseEmployee-Entry-Search"}
									autoFocus
									hintText={strings.search}
									value={this.state.search}
									onChange={e =>
										this.setState({
											search: e.target.value,
											offset: 0,
											loading: true,
											selectedEmployee: null,
											employeeConstraints: []
										}, () =>
											this.handleSearch()
										)
									}
									style={{ marginRight: 20, width: "90%", marginLeft: 15 }}
								/>
								<Clear />
							</div>
							<div style={{ textAlign: "center", }}>
								{this.state.loading ?
									<CircularProgress /> :
									this.state.limitByJobShortlist ?
										<Table
											onCellClick={rowNumber => this.setState({ selectedEmployee: this.state.candidates[rowNumber], employeeConstraints: [] })}
											multiSelectable={false}
										>
											<TableHeader>
												<TableRow className="ShortSelectBoxTableHeader">
													<TableHeaderColumn className="TextOverflow" title={strings.Name} style={{ width: 100, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Name}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Missing_Badges} style={{ width: 70, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Missing_Badges}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Miles} style={{ width: 70, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Miles}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Scheduled_Hours} style={{ textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Scheduled_Hours}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Available} style={{ textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Available}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Archive} style={{ width: 60, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Archive}</TableHeaderColumn>
												</TableRow>
											</TableHeader>
											<TableBody showRowHover deselectOnClickaway={false}>
												{(this.state.candidates as IWorkPoolEmployee[]).map((row, i) => {
													return (
														<TableRow data-intercom-target={`ChooseEmployee-SelectEmployee${i}`} key={row.Id} selected={this.state.selectedEmployee === row} className="ShortSelectBoxTableRow">
															<TableRowColumn style={{ width: 100, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }} title={row.EmployeeName}>
																{row.EmployeeName}
															</TableRowColumn>

															<TableRowColumn style={{ width: 70, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>{this.missingBadges(row.MissingBadgeNames)}</TableRowColumn>
															<TableRowColumn style={{ width: 70, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}></TableRowColumn>
															<TableRowColumn style={{ textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}></TableRowColumn>
															<TableRowColumn style={{ textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>
																{row.Available &&
																	<FontIcon className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
																}
															</TableRowColumn>
															<TableRowColumn style={{ width: 60, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>
																{row.ArchivedByUserIds && !row.ArchivedByUserIds.includes(AuthenticationStore.getLoggedInUser().Id) &&
																	<FontIcon
																		className="fa fa-thumbs-down"
																		style={{ fontSize: 33, color: "#95989a" }}
																	/>
																}
															</TableRowColumn>
														</TableRow>
													)
												})}
											</TableBody>
											<TableFooter>
												<TableRow className="TableFooter" style={{ borderBottom: 0 }}>
													<TableRowColumn style={{ float: "right" }}>
														<IconButton
															data-intercom-target={"ChooseEmployee-Btn-OpenFistPage"}
															disabled={this.state.offset === 0}
															onClick={() => this.offsetChange(0)}
														>
															<FontIcon className="fa fa-step-backward" />
														</IconButton>
														<IconButton
															data-intercom-target={"ChooseEmployee-Btn-OpenPreviousPage"}
															disabled={this.state.offset === 0}
															onClick={() => this.offsetChange(this.state.offset - this.limit)}
														>
															<FontIcon className="fa fa-chevron-left" />
														</IconButton>
														<IconButton
															data-intercom-target={"ChooseEmployee-Btn-OpenNextPage"}
															disabled={this.state.offset + this.limit >= this.state.total}
															onClick={() => this.offsetChange(this.state.offset + this.limit)}
														>
															<FontIcon className="fa fa-chevron-right" />
														</IconButton>
														<IconButton
															data-intercom-target={"ChooseEmployee-Btn-OpenLastPage"}
															disabled={this.state.offset + this.limit >= this.state.total}
															onClick={() => this.offsetChange(this.state.total - (this.state.total % this.limit === 0 ? this.limit : (this.state.total % this.limit)))}
														>
															<FontIcon className="fa fa-step-forward" />
														</IconButton>
													</TableRowColumn>
												</TableRow>
											</TableFooter>
										</Table> :
										<Table
											onCellClick={rowNumber => this.setState({ selectedEmployee: this.state.candidates[rowNumber], employeeConstraints: [] })}
											multiSelectable={false}
										>
											<TableHeader>
												<TableRow className="ShortSelectBoxTableHeader">
													<TableHeaderColumn style={{ width: 40, textAlign: "center", paddingLeft: 5, paddingRight: 5 }} />
													<TableHeaderColumn className="TextOverflow" title={strings.Name} style={{ width: 100, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Name}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Rank} style={{ width: 35, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Rank}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Employee_ID} style={{ width: 70, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Employee_ID}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Punch_Hierarchy} style={{ width: 100, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Punch_Hierarchy}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Default_Hierarchy} style={{ width: 120, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Default_Hierarchy}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Scheduled} style={{ width: 60, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Scheduled}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Worked} style={{ width: 40, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Worked}</TableHeaderColumn>
													<TableHeaderColumn className="TextOverflow" title={strings.Worked_and_Scheduled} style={{ width: 100, textAlign: "center", paddingLeft: 5, paddingRight: 5 }}>{strings.Worked_and_Scheduled}</TableHeaderColumn>
												</TableRow>
											</TableHeader>
											<TableBody showRowHover deselectOnClickaway={false}>
												{(this.state.candidates as IShiftCandidate[]).map((row, i) => {
													const name = `${row.FirstName} ${row.LastName}`;
													const totalWorkedAndScheduledHours = row.HoursWorked + row.HoursScheduled;
													const defaultHierarchyName = hierarchyHelper.getFullyQualifiedLevelNameById(row.DefaultHierarchyItemId)
													const defaultPunchHierarchyName = hierarchyHelper.getFullyQualifiedLevelNameById(row.DefaultPunchHierarchyId)
													return (
														<TableRow data-intercom-target={`ChooseEmployee-SelectEmployee${i}`} key={row.Id} selected={this.state.selectedEmployee === row} className="ShortSelectBoxTableRow">
															<TableRowColumn style={{ width: 40, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>
																{row.ProfileImageS3Url && (
																	<ImageLoader
																		url={row.ProfileImageS3Url}
																		fixedHeight={75}
																		avatar={true}
																		src={"/users/profileimage/" + row.ProfileImageS3Url}
																		key={`smallImg-${i}`}
																	/>
																)}
															</TableRowColumn>
															<TableRowColumn style={{ width: 100, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }} title={name}>
																{name}
															</TableRowColumn>
															<TableRowColumn style={{ width: 35, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>{row.ScheduleWeight}</TableRowColumn>
															<TableRowColumn style={{ width: 70, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>{row.ExternalId}</TableRowColumn>
															<TableRowColumn style={{ width: 100, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }} title={defaultHierarchyName}>
																{defaultHierarchyName}
															</TableRowColumn>
															<TableRowColumn style={{ width: 120, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }} title={defaultPunchHierarchyName}>
																{defaultPunchHierarchyName}
															</TableRowColumn>
															<TableRowColumn style={{ width: 60, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>{row.HoursScheduled}</TableRowColumn>
															<TableRowColumn style={{ width: 40, textAlign: "center", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>{row.HoursWorked}</TableRowColumn>
															<TableRowColumn
																style={{
																	width: 100,
																	textAlign: "center",
																	cursor: "pointer",
																	paddingLeft: 5,
																	paddingRight: 5,
																	color: totalWorkedAndScheduledHours > 80 ? red900 : green500
																}}
															>
																{totalWorkedAndScheduledHours}
															</TableRowColumn>
														</TableRow>
													)
												})}
											</TableBody>
											<TableFooter>
												<TableRow className="TableFooter" style={{ borderBottom: 0 }}>
													<TableRowColumn style={{ float: "right" }}>
														<IconButton
															data-intercom-target={"ChooseEmployee-Btn-OpenFistPage"}
															disabled={this.state.offset === 0}
															onClick={() => this.offsetChange(0)}
														>
															<FontIcon className="fa fa-step-backward" />
														</IconButton>
														<IconButton
															data-intercom-target={"ChooseEmployee-Btn-OpenPreviousPage"}
															disabled={this.state.offset === 0}
															onClick={() => this.offsetChange(this.state.offset - this.limit)}
														>
															<FontIcon className="fa fa-chevron-left" />
														</IconButton>
														<IconButton
															data-intercom-target={"ChooseEmployee-Btn-OpenNextPage"}
															disabled={this.state.offset + this.limit >= this.state.total}
															onClick={() => this.offsetChange(this.state.offset + this.limit)}
														>
															<FontIcon className="fa fa-chevron-right" />
														</IconButton>
														<IconButton
															data-intercom-target={"ChooseEmployee-Btn-OpenLastPage"}
															disabled={this.state.offset + this.limit >= this.state.total}
															onClick={() => this.offsetChange(this.state.total - (this.state.total % this.limit === 0 ? this.limit : (this.state.total % this.limit)))}
														>
															<FontIcon className="fa fa-step-forward" />
														</IconButton>
													</TableRowColumn>
												</TableRow>
											</TableFooter>
										</Table>
								}
							</div>
						</div>
					</Tab>
					{AccountStore.getCRMEnabled() &&
						<Tab label={strings.Gig_Pool} value="worker_pool">
							<div style={{ padding: 20, overflow: "auto" }}>
								<ShiftWorkerPool
									readonly={false}
									assignPatternTemplate={this.props.assignPatternTemplate}
									replaceSchedulePatternTemplate={this.props.replaceSchedulePatternTemplate}
									handleChooseEmployee={selectedEmployee => this.setState({ selectedEmployee, employeeConstraints: [] })}
									assignEmployeeToShift={(employee) => {
										this.setState({ selectedEmployee: employee, }, () => { this.setState({ confirmCandidate: true }) })
									}}
								/>
							</div>
						</Tab>
					}
				</Tabs>
				<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", padding: 20, }}>
					{this.availablePatternTemplates.length > 1 && <FlatButton
						data-intercom-target={"ChooseEmployee-Btn-SelectAdditionalDays"}
						label={strings.Select_Additional_Days}
						primary
						keyboardFocused
						onClick={() => this.setState({ openSelectAdditionalDaysDialog: true })}
					/>}
					<FlatButton
						data-intercom-target={"ChooseEmployee-Btn-CloseEmployeeSelectionDialog"}
						label={strings.Cancel}
						secondary
						onClick={this.props.handleClose}
					/>
					<FlatButton
						data-intercom-target={"ChooseEmployee-Btn-SaveSelectedEmployee"}
						label={strings.OK}
						primary
						keyboardFocused
						disabled={this.state.selectedEmployee ? false : true}
						onClick={this.handleClickSave}
					/>
				</div>
				{this.state.openSelectAdditionalDaysDialog &&
					<SelectAdditionalDaysDialog
						availablePatternTemplates={this.availablePatternTemplates}
						fixedDay={this.props.assignPatternTemplate}
						selectedDays={this.state.selectedDays}
						handleClose={() => this.setState({ openSelectAdditionalDaysDialog: false })}
						handleSave={selectedDays => this.setState({ selectedDays, openSelectAdditionalDaysDialog: false })}
					/>
				}
				{this.state.employeeConstraints.length > 0 &&
					<CheckAssignedEmployeeConstraintDialog
						assignedEmployeeConstraints={{
							Employee: {
								FirstName: this.getSelectedEmployeeName(),
								LastName: "",
								ExternalId: "",
								ProfileImageS3Url: this.state.selectedEmployee.hasOwnProperty("EmployeeName") ? null : (this.state.selectedEmployee as IShiftCandidate).ProfileImageS3Url
							} as IUserDocument,
							SchedulePatternTemplate: this.props.assignPatternTemplate,
							Constraints: this.state.employeeConstraints

						} as IAssignedEmployeeConstraint}
						handleClose={() => this.setState({ employeeConstraints: [] })}
						handleSave={this.state.employeeConstraints.find(i => i.Severity === EScheduleConstraintSeverity.Error) ?
							null :
							() => this.handleAssign()
						}
					/>
				}
				<ConfirmDialog title="Confirm Candidate" confirmBtnLabel="Yes" confirmContent="Are you sure you want to assign this candidate to this shift?" open={this.state.confirmCandidate} close={() => this.setState({ confirmCandidate: false })} confirm={() => {
					this.setState({ confirmCandidate: false }, () => {
						this.handleClickSave()
					})
				}} />
			</Drawer>
		)
	}
}
